import { Component } from '@angular/core';
import { PipelineSettingsService } from './pipeline-settings.service';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ActivatedRoute, Router } from '@angular/router';
import { PipelineInterface } from '@vendasta/sales-opportunities';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { settingsPagePreviousUrl } from './constants';

@Component({
  selector: 'sales-ui-pipeline-settings',
  templateUrl: './pipeline-settings.component.html',
  styleUrl: './pipeline-settings.component.scss',
  standalone: false,
})
export class PipelineSettingsComponent {
  protected readonly pipelines$: Observable<PipelineInterface[]>;
  protected previousPageUrl = settingsPagePreviousUrl;
  constructor(
    private readonly pipelineSettingsService: PipelineSettingsService,
    private readonly snackbarService: SnackbarService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {
    const reloadRequests$ = this.pipelineSettingsService.redirectRequests$;
    const partnerId$ = this.pipelineSettingsService.partnerId$;
    const pipelineId$ = this.pipelineSettingsService.pipelineId$;
    this.pipelines$ = this.listAndSortPipelines(pipelineId$, partnerId$, reloadRequests$);

    const pipelinesMap$ = this.pipelines$.pipe(
      map((pipelines) => new Map(pipelines.map((pipeline) => [pipeline.pipelineId, pipeline]))),
    );
    pipelinesMap$.pipe(takeUntilDestroyed(), withLatestFrom(pipelineId$)).subscribe(([pipelines, pipelineId]) => {
      const found = this.setCurrentPipeline(pipelineId, pipelines);
      if (!found) {
        const fallbackFound = this.setCurrentPipelineToFallback(pipelines);
        if (!fallbackFound) {
          this.setCurrentPipelineToEmpty();
        }
      }
    });
  }

  private setCurrentPipelineToFallback(pipelines: Map<string, PipelineInterface>): boolean {
    if (pipelines.size > 0) {
      const fallback: PipelineInterface = pipelines.values().next().value;
      const fallbackId = fallback.pipelineId;
      this.loadPipeline(fallbackId).then((_) => {
        this.pipelineSettingsService.currentPipeline$$.next(fallback);
      });
      return true;
    }
    return false;
  }

  private setCurrentPipelineToEmpty(): void {
    this.loadPipeline('create').then((_) => {
      this.pipelineSettingsService.currentPipeline$$.next({});
    });
  }

  private setCurrentPipeline(pipelineId: string, pipelines: Map<string, PipelineInterface>): boolean {
    const found = pipelines.get(pipelineId);
    if (found) {
      this.pipelineSettingsService.currentPipeline$$.next(found);
      return true;
    }
    return false;
  }

  private listAndSortPipelines(
    pipelineId$: Observable<string>,
    partnerId$: Observable<string>,
    reloadRequests$: Observable<boolean>,
  ): Observable<PipelineInterface[]> {
    return combineLatest([pipelineId$, partnerId$, reloadRequests$]).pipe(
      switchMap(([_, partnerId]) => this.pipelineSettingsService.list(partnerId)),
      map((pipelines) => pipelines.filter((pipeline) => pipeline.pipelineId !== 'DEFAULT')),
      map((pipelines) => pipelines.sort((a, b) => (a.name < b.name ? -1 : 1))),
    );
  }

  private async loadPipeline(pipelineId: string): Promise<void> {
    this.router.navigate([`${pipelineId}`], {
      relativeTo: this.route,
    });
  }

  protected async createNewPipeline(): Promise<void> {
    const newPipelineId = await firstValueFrom(this.pipelineSettingsService.createDefaultPipeline());
    if (newPipelineId) {
      this.snackbarService.openSuccessSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.CREATE_SUCCESS');
      await this.loadPipeline(newPipelineId);
    } else {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.CREATE_FAILED');
    }
  }
}
